import { resolveBatteryStandardStatus, type BatteryLike } from '@/shared/helper/battery-status';
import { formatMovementOffReasonName } from '@/domains/widgets/device-group-status-display';

const ALERT_NOTIFIER_A_CONTROL_LABELS: Record<string, Record<string, string>> = {
  '01': { '01': '주기 설정 30분', '02': '주기 설정 1시간', '03': '주기 설정 2시간', '04': '주기 설정 4시간', '05': '주기 설정 24시간' },
  '02': { '01': 'GPS 시작', '02': 'GPS 수신 대기' },
};

/** 외부 projection의 속성은 객체·유한 숫자만 화면 계산에 사용합니다. */
export function presentationObject(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** 등록 모델이 과거18이어도 실제 침수 패킷 카테고리30은 구조물입니다. */
export function isStructuralFloodSensor(sensor: Record<string, unknown>): boolean {
  if (sensor.rx_profile_code !== 'SAFETY_GUARD_MINI_FLOODING') return false;
  const dto = presentationObject(presentationObject(sensor.attributes).dto);
  const raw = typeof dto.rawData === 'string' ? dto.rawData.toUpperCase() : '';
  return Number(sensor.product_sensor_type) === 30 || Number(dto.sensorCategory) === 30 || (/^3CF1[0-9A-F]{52}3E$/.test(raw) && raw.slice(8, 10) === '1E');
}

function numeric(value: unknown): number | undefined {
  if (typeof value === 'string' && value.trim()) value = Number(value);
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** 대표 장비가 확정되지 않으면 구성 센서의 배터리를 대신 사용하지 않습니다. */
export function resolvePrimarySensor(product: Record<string, unknown>): Record<string, unknown> | undefined {
  const components = Array.isArray(product.components) ? product.components.map(presentationObject) : [];
  const primary = components.filter(component => component.role === 'PRIMARY');
  if (primary.length !== 1) return undefined;
  const id = primary[0].product_unit_id ?? primary[0].id;
  const sensors = Array.isArray(product.sensors) ? product.sensors.map(presentationObject) : [];
  const matches = sensors.filter(sensor => (sensor.product_unit_id ?? sensor.id) === id);
  return matches.length === 1 ? matches[0] : undefined;
}

/** 배터리 없는 CCTV는 같은 제품에 명시적으로 연결된 유일한 활성 전원 모듈을 사용합니다. */
export function resolveProductBatterySensor(product: Record<string, unknown>): Record<string, unknown> | undefined {
  const primary = resolvePrimarySensor(product);
  // 영상 점검 전에도 카탈로그의 CCTV 모델을 식별하며 일반 제품의 대표 센서 선택은 유지합니다.
  if (!primary || (primary.sensor_model_code !== 'MOBILE_PTZ_CAMERA' && typeof presentationObject(primary.camera_health).status !== 'string')) return primary;
  const components = Array.isArray(product.components) ? product.components.map(presentationObject) : [];
  const primaryId = primary.product_unit_id ?? primary.id;
  if (
    product.status !== 'ACTIVE' ||
    typeof product.field_uid !== 'string' ||
    !product.field_uid.trim() ||
    primary.field_uid !== product.field_uid ||
    typeof primaryId !== 'string' ||
    !primaryId.trim() ||
    primary.status !== 'ACTIVE' ||
    primary.component_state !== 'INSTALLED_ENABLED' ||
    components.find(component => component.role === 'PRIMARY')?.status !== 'ACTIVE'
  )
    return undefined;

  const battery = sensorBatteryPresentation(primary);
  if (battery.percent !== undefined || battery.voltage !== undefined || battery.status !== 'unknown') return primary;
  // 값이 있는 모듈만 먼저 고르면 복수 연결을 숨기므로 구성의 유일성을 배터리 유무보다 먼저 검사합니다.
  const powerComponents = components.filter(
    component =>
      component.role === 'COMPONENT' && component.component_type === 'POWER_CONTROL' && component.status === 'ACTIVE' && component.component_state === 'INSTALLED_ENABLED'
  );
  if (powerComponents.length !== 1) return undefined;
  const id = powerComponents[0].product_unit_id ?? powerComponents[0].id;
  if (typeof id !== 'string' || !id.trim()) return undefined;
  const sensors = Array.isArray(product.sensors) ? product.sensors.map(presentationObject) : [];
  const matches = sensors.filter(sensor => (sensor.product_unit_id ?? sensor.id) === id);
  if (matches.length !== 1) return undefined;
  const power = matches[0];
  return power.field_uid === product.field_uid && power.status === 'ACTIVE' && power.component_state === 'INSTALLED_ENABLED' && power.rx_profile_code === 'POWER_CONTROL_MODULE'
    ? power
    : undefined;
}

/** 전압·실측 잔량·상태를 구분하며 상태에서 임의 백분율을 만들지 않습니다. */
export function sensorBatteryPresentation(sensor: Record<string, unknown> | undefined) {
  const dto = presentationObject(presentationObject(sensor?.attributes).dto);
  const batt = presentationObject(dto.batt);
  const rawVoltage = numeric(sensor?.battery_volt ?? batt.volt);
  const voltage = rawVoltage !== undefined && rawVoltage > 0 ? rawVoltage : undefined;
  const rawPercent = numeric(batt.percent);
  const percent = rawPercent !== undefined && rawPercent >= 0 && rawPercent <= 100 && !(rawPercent === 0 && voltage === undefined) ? rawPercent : undefined;
  const status = resolveBatteryStandardStatus({ ...batt, volt: voltage, percent } as BatteryLike);
  const labels = { unknown: '정보없음', low: '부족', normal: '정상', good: '좋음', full: '완충' };
  const value = [percent !== undefined ? `${percent}%` : null, voltage !== undefined ? `${Number(voltage.toFixed(2))}V` : null].filter(Boolean).join(' · ');
  return { percent, voltage, status, label: value ? `${value} · ${labels[status]}` : labels[status] };
}

/** 현재 수신한 이동·OFF 사유를 표시하고 제어 코드는 처리 프로필과 함께 해석합니다. */
function sensorReceivedStateLabel(sensor: Record<string, unknown>): string | undefined {
  const attributes = presentationObject(sensor.attributes);
  const evidence = presentationObject(attributes.state_evidence);
  // 간접 감지와 부모 OFF 사유는 원본 수신 DTO를 바꾸지 않고 현재 연결 상태로 표시합니다.
  if (sensor.availability === 'OFFLINE') {
    if (evidence.source === 'LIVE_CHECK' && evidence.reason === 'A1_MISSING') return '진출';
    if (evidence.source === 'PARENT' && evidence.reason === 'PARENT_OFF') return '부모 OFF';
  }
  const dto = presentationObject(attributes.dto);
  const stateName = typeof dto.sensorStateName === 'string' ? dto.sensorStateName.trim() : '';
  if (!stateName) return undefined;
  if (stateName === '작업구역 진입') return '진입';
  if (stateName === '작업구역 진출') return '진출';
  const stateCode = typeof dto.sensorState === 'string' ? dto.sensorState.trim().toUpperCase() : '';
  if (stateName.toUpperCase() === 'UNKNOWN') {
    // 이전 Central이 저장한 미세먼지 RTC 00도 통신 상태와 별도로 측정 유형만 표시합니다.
    return sensor.rx_profile_code === 'DUST_LEVEL_MONITOR' && dto.state === 'MEASURE' && stateCode === '00' ? '측정' : '상태 확인 필요';
  }
  // OFF 사유는 이번 수신이 진출(10)일 때만 적용하여 재진입·안전 이벤트에 과거 사유를 붙이지 않습니다.
  if (stateName === '진출' && stateCode === '10') {
    const reason = formatMovementOffReasonName({
      movement_off_reason: typeof dto.movementOffReason === 'string' ? dto.movementOffReason : undefined,
      movement_off_reason_name: typeof dto.movementOffReasonName === 'string' ? dto.movementOffReasonName : undefined,
      movement_display_label: typeof dto.movementDisplayLabel === 'string' ? dto.movementDisplayLabel : undefined,
    });
    if (reason && ['진출', '사용자OFF', '저전력OFF'].includes(reason)) return reason;
  }
  if (stateName === 'LIVE CHECK') return '연결 센서 확인';
  if (!/^[\da-f]{2}$/i.test(stateName)) return stateName;

  // 처리 프로필을 우선하며 표시 이름만으로 다른 센서의 같은 코드를 A1 상태로 해석하지 않습니다.
  const profile = sensor.rx_profile_code ?? sensor.sensor_model_code;
  if (profile === 'ALERT_NOTIFIER_A' || profile === 'ALERT_NOTIFIER_A_V1') {
    const group = typeof dto.sensorState === 'string' ? dto.sensorState.trim().toUpperCase() : '';
    if (group === '0C') return '시간 설정 요청';
    const label = ALERT_NOTIFIER_A_CONTROL_LABELS[group]?.[stateName.toUpperCase()];
    if (label) return label;
  }
  return '상태 확인 필요';
}

/** 실제 수신한 배터리·GPS·장비 상태만 표시하고 없는 계측 항목은 만들지 않습니다. */
export function sensorSupplementaryPresentation(sensor: Record<string, unknown>) {
  const attributes = presentationObject(sensor.attributes);
  const evidence = presentationObject(attributes.state_evidence);
  const relayReceivedAt =
    sensor.rx_profile_code === 'ALERT_NOTIFIER_A' && evidence.source === 'RELAY' && typeof evidence.received_at === 'string' && Number.isFinite(Date.parse(evidence.received_at))
      ? evidence.received_at
      : undefined;
  const battery = sensorBatteryPresentation(sensor);
  const latitude = numeric(attributes.latitude);
  const longitude = numeric(attributes.longitude);
  // GPS 미수신 기본값인 (0, 0)은 위치 카드에서 숨기고 한 좌표만 0인 위치는 유지합니다.
  const position =
    latitude !== undefined && longitude !== undefined && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180 && !(latitude === 0 && longitude === 0)
      ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
      : undefined;
  return {
    battery,
    position,
    stateLabel: sensorReceivedStateLabel(sensor),
    relayReceivedAt,
  };
}
